import React from "react"
import {Icon, Menu} from "semantic-ui-react"
import {AppState} from "../app"
import {ScreenSize, Media} from "../utils/media-utils"
import {FormattedMessage} from "react-intl"
import {IndiInfo, JsonGedcomData} from "../topola"
import {ContactForm} from "../contact"
import {I18nLanguageMenu} from "./i18n-language-menu"
import {ChartMenu} from "./chart-menu"
import {SearchBar} from "./search-bar"
import {useLocation} from "react-router"
import {CreatorMenu} from "./creator-menu"
import {PricingModal} from "../pricing"
import {PrivacyModal} from "../privacy"
import {useMediaQuery} from "react-responsive"


export interface TopBarProps {
    appState?: AppState
    searchData?: JsonGedcomData
    unsavedChanges?: boolean
    loading?: boolean
    allowConvert?: boolean
    howItWorksOpen?: boolean
    contactModalOpen: boolean
    pricingModalOpen: boolean
    checkoutSuccess: boolean
    privacyModalOpen?: boolean
    eventHandlers: EventHandlers
}

export interface EventHandlers {
    onHome?: () => void
    onSelection?: (indiInfo: IndiInfo) => void
    onDownloadPng?: () => void
    onDownloadSvg?: () => void
    onDownloadGedcom?: () => void
    onResetView?: () => void
    onEditGedcom?: () => void
    onImportXlsx?: (file: File, event: string) => void
    onExportXlsx?: () => void
    onConvert?: () => void
    setHowItWorksOpen?: (open: boolean) => void
    setCheckoutSuccess: (open: boolean) => void
    setContactModalOpen: (open: boolean) => void
    setPricingModalOpen: (open: boolean) => void
    setPrivacyModalOpen: (open: boolean) => void
    onI18nLanguage: (lang: string) => void
}

export function TopBar({
    appState,
    searchData,
    howItWorksOpen,
    unsavedChanges,
    contactModalOpen,
    pricingModalOpen,
    checkoutSuccess,
    privacyModalOpen,
    loading,
    allowConvert,
    eventHandlers
}: TopBarProps) {
    const isDesktop = useMediaQuery({ query: "(min-width: 1000px)" })
    const location = useLocation()
    const showingCreator = location.pathname === "/create"
    const showingChart = location.pathname === "/view" && (appState === AppState.LOADING || appState === AppState.SHOWING_CHART)

    function desktopMenu() {
        return <Menu as={Media} greaterThanOrEqual="desktop" attached="top" className="ui linkblue menu" size="large">

            {/* HOME | HOW IT WORKS | SAVE/LOAD | CONVERT */}
            {showingCreator && <CreatorMenu
                howItWorksOpen={howItWorksOpen!}
                unsavedChanges={unsavedChanges!}
                loading={loading!}
                allowConvert={allowConvert!}
                eventHandlers={eventHandlers!}
            />}

            {/* HOME | DOWNLOAD | RESET VIEW | EDIT */}
            {showingChart && <ChartMenu loading={loading!} eventHandlers={eventHandlers}/>}

            {!showingChart && location.pathname !== "/privacy" && <>
                {/* CONTACT */}
                <Menu.Item key="contact" className="right" onClick={() => eventHandlers.setContactModalOpen(true)}>
                    <Icon name="mail"/><FormattedMessage id="menu.contact"/>
                </Menu.Item>
                <ContactForm open={contactModalOpen} onClose={() => eventHandlers.setContactModalOpen(false)} onI18nLanguage={eventHandlers.onI18nLanguage}/>
                {/* SERVICES & PRICING */}
                {!showingCreator && <>
                    <Menu.Item key="pricing" onClick={() => eventHandlers.setPricingModalOpen(true)}>
                        <Icon name="credit card"/><FormattedMessage id="menu.pricing"/>
                    </Menu.Item>
                    <PricingModal
                        screenSize={ScreenSize.LARGE}
                        open={pricingModalOpen}
                        onClose={() => eventHandlers.setPricingModalOpen(false)}
                        checkoutSuccess={checkoutSuccess}
                        setCheckoutSuccess={eventHandlers.setCheckoutSuccess}
                        setContactModalOpen={eventHandlers.setContactModalOpen}
                    />
                    {/* PRIVACY */}
                    <Menu.Item key="privacy" onClick={() => eventHandlers.setPrivacyModalOpen(true)}>
                        <Icon name="shield"/><FormattedMessage id="menu.privacy"/>
                    </Menu.Item>
                    <PrivacyModal
                        open={privacyModalOpen!}
                        onClose={() => eventHandlers.setPrivacyModalOpen(false)}
                        setContactModalOpen={eventHandlers.setContactModalOpen}
                    />
                </>}
            </>}

            {/* SEARCH */}
            {showingChart && <SearchBar data={searchData!} onSelection={eventHandlers.onSelection!}/>}

            {/* I18N */}
            <I18nLanguageMenu onI18nLanguage={eventHandlers.onI18nLanguage}/>
        </Menu>
    }

    function mobileMenu() {
        return <Menu id="top-menu" as={Media} lessThan="desktop" attached="top" size="large">

            {showingChart && <>
                {/* HOME */}
                <Menu.Item key="home" className="left" onClick={eventHandlers.onHome}>
                    <Icon name="home"/><FormattedMessage id="menu.home"/>
                </Menu.Item>
            </>}

            {/* CONTACT */}
            <Menu.Item key="contact" className="right" onClick={() => eventHandlers.setContactModalOpen(true)}>
                <Icon name="mail"/><FormattedMessage id="menu.contact"/>
            </Menu.Item>
            <ContactForm open={contactModalOpen} onClose={() => eventHandlers.setContactModalOpen(false)} onI18nLanguage={eventHandlers.onI18nLanguage}/>

            {!showingChart && <>
                {/* SERVICES & PRICING */}
                <Menu.Item key="pricing" onClick={() => eventHandlers.setPricingModalOpen(true)}>
                    <Icon name="credit card"/><FormattedMessage id="menu.pricing"/>
                </Menu.Item>
                <PricingModal
                    screenSize={ScreenSize.SMALL}
                    open={pricingModalOpen}
                    onClose={() => eventHandlers.setPricingModalOpen(false)}
                    checkoutSuccess={checkoutSuccess}
                    setCheckoutSuccess={eventHandlers.setCheckoutSuccess}
                    setContactModalOpen={eventHandlers.setContactModalOpen}
                />
            </>}


            {/* I18N LANGUAGE */}
            <I18nLanguageMenu onI18nLanguage={eventHandlers.onI18nLanguage}/>
        </Menu>
    }

    return isDesktop ? desktopMenu() : mobileMenu()
}
